// External Imports
import { Fragment, useState } from "react";
import { MdAdd, MdClose } from "react-icons/md";

// Internal Imports
import { isoDateToString } from "../../util/format-date";
import Modal from "../UI/Modal";
import Heading from "../UI/Heading";
import DetailPair from "../UI/DetailPair";
import NoRecordsFound from "../UI/NoRecordsFound";
import { requestAdminAPI } from "../../api/posh-api";
import LoadingSpinner from "../UI/LoadingSpinner";
import SelectPair from "../UI/SelectPair";

/** ========================================================================
 * * PaymentMethods Component
 * This component renders a modal displaying list of customer's payment methods.
 * ========================================================================== */

export default function PaymentMethods({
  open,
  setOpen,
  methods = [],
  defaultId,
  refresh,
  customerId,
}) {
  // Initialize States
  const [loading, setLoading] = useState(false);
  const [add, setAdd] = useState(false);

  /* ====================== CLICK HANDLER ====================== */
  const clickHandler = async (accountId) => {
    if (!accountId) return;
    try {
      setLoading(true);
      await requestAdminAPI("check-balance", {
        accountId: accountId,
      });
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ====================== ADD PAYMENT METHOD ====================== */
  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const card = {
        number: e.target.cardNumber.value,
        exp_month: e.target.expMonth.value,
        exp_year: e.target.expYear.value,
        cvc: e.target.cvc.value,
      };
      await requestAdminAPI("add-payment-method", {
        customerId: customerId,
        card: card,
        setDefault: e.target.setDefault.value === "Yes",
      });
      setAdd(false);
      setOpen(false);
      refresh();
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="modal grid gap-8 lg:w-[50vw]">
        <Heading
          title="Payment Methods"
          text="Default payment method is yellow, deleted payment methods are red. If a payment method is a bank, you can check the balance by clicking on it."
          isBackArrow={false}
        >
          <MdAdd className="icon" onClick={() => setAdd(true)} />
          <MdClose className="icon" onClick={() => setOpen(false)} />
        </Heading>
        {add && (
          <form onSubmit={submitHandler} className="info">
            <div className="lg:col-span-4">
              <DetailPair
                label="Card Number"
                edit={true}
                inputOptions={{
                  type: "number",
                  id: "cardNumber",
                  maxLength: 16,
                  required: true,
                }}
              />
            </div>
            <DetailPair
              label="Expiration Month"
              edit={true}
              inputOptions={{
                type: "number",
                id: "expMonth",
                minLength: 2,
                maxLength: 2,
                required: true,
                placeholder: "MM",
              }}
            />
            <DetailPair
              label="Expiration Year"
              edit={true}
              inputOptions={{
                type: "number",
                id: "expYear",
                maxLength: 4,
                minLength: 4,
                required: true,
                placeholder: "YYYY",
              }}
            />
            <DetailPair
              label="CVC"
              edit={true}
              inputOptions={{
                type: "number",
                id: "cvc",
                required: true,
              }}
            />
            <SelectPair
              label="Set Default"
              edit={true}
              options={["No", "Yes"]}
              inputOptions={{
                id: "setDefault",
                required: true,
                defaultValue: "No",
              }}
            />
            {loading ? (
              <div className="mt-8 lg:col-span-4">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 mt-8 lg:col-span-4">
                <div className="btn-secondary" onClick={() => setAdd(false)}>
                  Cancel
                </div>
                <button className="btn-primary" type="submit">
                  Confirm
                </button>
              </div>
            )}
          </form>
        )}

        {!add && (
          <Fragment>
            {methods.length > 0 ? (
              <div className="list max-h-[400px]">
                {methods.map((method, idx) => (
                  <div
                    key={idx}
                    onClick={() => clickHandler(method.accountId)}
                    className={`grid info pill lg:grid-cols-5 ${
                      defaultId === method.id
                        ? "border-yellow-600"
                        : method.deleted
                        ? "border-red-600"
                        : ""
                    }`}
                  >
                    <DetailPair
                      label="Type"
                      value={method.type.toUpperCase()}
                    />
                    <div className="lg:col-span-2">
                      <DetailPair
                        label="Brand"
                        value={method.brand.toUpperCase()}
                      />
                    </div>
                    <DetailPair
                      label="Last 4"
                      value={method.last4}
                      classes="font-semibold text-stone-200"
                    />
                    <DetailPair
                      label="Date Added"
                      value={isoDateToString(method.dateCreated)}
                      classes="font-semibold text-stone-200"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <NoRecordsFound />
            )}
            {loading && (
              <div className="flex items-center justify-center space-x-4">
                <LoadingSpinner />
                <p className="text-sm text-stone-400">
                  Checking Bank Balance...
                </p>
              </div>
            )}
          </Fragment>
        )}
      </div>
    </Modal>
  );
}
